<template>
    <slot v-bind="{
        ...state,
        sortOptions,
        setSortBy,
        updateFacet,
        setSelectedPrice,
        setFiltersDialog,
        openFiltersDialog,
        closeFiltersDialog,
    }" />
</template>

<script setup>
import { onBeforeMount, reactive } from 'vue';
import { get, cloneDeep, flatten, uniq, map, min, max, sortBy } from 'lodash';
import Fuse from 'fuse.js';
import { startOfDay } from 'date-fns';

import { getSeries, getSeriesById } from '../services/inventory';
import { getSeriesBookingState } from '../services/booking';
import zonedDate from '../utils/zoned-date';

const today = startOfDay(zonedDate());

const props = defineProps({
    series: {
        type: Array,
        default: [],
    },
})

const customSort = {
    Monday: 0,
    Tuesday: 1,
    Wednesday: 2,
    Thursday: 3,
    Friday: 4,
    Saturday: 5,
    Sunday: 6,
}

const state = reactive({
    isLoading: true,
    hasError: false,
    results: [],
    filters: [],
    totalResults: 0,
    canReset: false,
    minPrice: null,
    maxPrice: null,
    selectedPrice: null,
    sortBy: 'default',
});

let series = null;
let fuse = null;
let filtersDialog = null

const sortOptions = [
    {
        value: 'default',
        label: 'Default',
    },
    {
        value: 'name-asc',
        label: 'Name: A–Z',
    },
    {
        value: 'price-asc',
        label: 'Price: Low–High',
    },
];

const setupFilters = () => {
    const items = [
        {
            name: 'types',
            title: 'Type of Package',
            field: 'filters.types',
            facets: [],
            isOpen: false,
        },
        {
            name: 'dayOfWeek',
            title: 'Day of the Week',
            field: 'filters.dayOfWeek',
            facets: [],
            isOpen: false,
        },
        {
            name: 'totalPerformances',
            title: 'Number of Events',
            field: 'filters.totalPerformances',
            facets: [],
            isOpen: false,
        },
        {
            name: 'timeOfDay',
            title: 'Time of Day',
            field: 'filters.timeOfDay',
            facets: [],
            isOpen: false,
        },
        // {
        //     name: 'featuring',
        //     title: 'Featuring',
        //     field: 'filters.featuring',
        //     facets: [],
        //     isOpen: false,
        // },
    ];

    state.filters = items.map(filter => {
        let facets = series.map(item => {
            return cloneDeep(get(item, filter.field));
        }).filter(facet => facet);

        facets = flatten(facets);

        facets = uniq(facets);

        filter.facets = facets.map(facet => {
            return {
                title: facet,
                isChecked: false,
                isDisabled: false,
            }
        });

        filter.facets = sortBy(filter.facets, facet => {
            if (facet.title in customSort) {
                return customSort[facet.title];
            }

            return facet.title;
        });

        return filter;
    });
}

const updateFuse = () => {
    fuse = new Fuse(series, {
        threshold: 0.30,
        useExtendedSearch: true,
        includeScore: false,
        shouldSort: false,
        keys: [
            'filters.types',
            'filters.dayOfWeek',
            'filters.totalPerformances',
            'filters.timeOfDay',
            'filters.featuring',
            'filters.priceFrom',
        ],
    });
}

const applySearchParams = initalLoad => {
    const searchParams = new URLSearchParams(window.location.search);

    state.filters = state.filters.map(filter => {
        filter.facets = filter.facets.map(facet => {
            facet.isChecked = searchParams.getAll(filter.name).indexOf(facet.title) !== -1;

            return facet;
        });

        if (initalLoad) {
            filter.isOpen = filter.facets.some(facet => {
                return facet.isChecked;
            });
        }

        return filter;
    });

    if (searchParams.has('priceFrom')) {
        state.selectedPrice = parseInt(searchParams.get('priceFrom'), 10) || state.minPrice;
    }

    if (searchParams.has('sortBy')) {
        state.sortBy = searchParams.get('sortBy');
    }

    processResults();
}

const updateSearchParams = () => {
    const searchParams = new URLSearchParams();

    for (const filter of state.filters) {
        for (const facet of filter.facets) {
            if (facet.isChecked) {
                searchParams.append(filter.name, facet.title);
            }
        }
    }

    if (state.selectedPrice) {
        searchParams.set('priceFrom', state.selectedPrice);
    }

    searchParams.set('sortBy', state.sortBy);

    const url = new URL(window.location);

    url.search = searchParams.toString();

    window.history.pushState(null, '', url.toString());
}

const buildQuery = filters => {
    let query = map(filters, (facets, filter) => {
        if (!facets.length) {
            return null;
        }

        return {
            '$or': facets.map(facet => {
                return {
                    [filter]: `="${facet}"`,
                }
            }),
        }
    });

    query = query.filter(item => {
        return item !== null;
    });

    if (state.selectedPrice && state.minPrice !== state.maxPrice) {
        const len = state.maxPrice - state.selectedPrice + 1;
        const prices = new Array(len);

        for (let i = 0; i < len; i++) {
            prices[i] = { 'filters.priceFrom': `=${state.selectedPrice + i}` };
        }

        if (prices.length) {
            query.push({
                '$or': prices,
            });
        }
    }

    if (state.searchQuery) {
        query.push({
            '$or': [
                {
                    'event.title': state.searchQuery,
                },
            ],
        });
    }

    return query;
}

const processResults = () => {
    const appliedFacets = {}

    for (const filter of state.filters) {
        appliedFacets[filter.field] = [];

        for (const facet of filter.facets) {
            if (facet.isChecked) {
                appliedFacets[filter.field].push(facet.title);
            }
        }
    }

    const appliedQuery = buildQuery(appliedFacets);

    let results = series;
    let canReset = false;

    if (appliedQuery.length) {
        results = fuse.search({
            '$and': appliedQuery,
        }).map(result => {
            return result.item;
        });

        canReset = true;
    }

    if (state.sortBy === 'name-asc') {
        results = sortBy(results, item => item.title);
    } else if (state.sortBy === 'price-asc') {
        results = sortBy(results, item => item.attributes?.custom?.price_from);
    } else {
        results = sortBy(results, item => item.index);
    }

    state.results = results;
    state.canReset = canReset;

    state.totalResults = state.results.length;

    state.filters = state.filters.map(filter => {
        filter.facets = filter.facets.map(facet => {
            if (!facet.isChecked) {
                const potentialFacets = cloneDeep(appliedFacets);

                potentialFacets[filter.field] = [facet.title];

                const potentialResults = fuse.search({
                    '$and': buildQuery(potentialFacets),
                });

                facet.isDisabled = !potentialResults.length;
            }

            return facet;
        });

        return filter;
    });
}

const setSortBy = event => {
    state.sortBy = event.target.value;

    processResults();
    updateSearchParams();
}

const setSelectedPrice = event => {
    state.selectedPrice = parseInt(event.target.value, 10);

    processResults();
    updateSearchParams();
}

const updateFacet = (eventFilter, eventFacet, event) => {
    state.filters = state.filters.map(filter => {
        filter.facets = filter.facets.map(facet => {
            if (filter.title === eventFilter.title && facet.title === eventFacet.title) {
                facet.isChecked = event.target.checked;
            }

            return facet;
        });

        return filter;
    });

    processResults();
    updateSearchParams();
}

const setFiltersDialog = element => {
    filtersDialog = element;
}

const openFiltersDialog = () => {
    filtersDialog.showModal();
}

const closeFiltersDialog = () => {
    filtersDialog.close();
}

onBeforeMount(() => {
    const promises = props.series.map(id => {
        return getSeriesById(id);
    });

    Promise.all(promises).then(responses => {
        return [ ...responses ];
    }).then(async seriesResponse => {
        series = seriesResponse.filter(item => {
            if (item.noLongerAvailable) {
                return false;
            }

            return +item.endDate > +today;
        }).map(item => {
            item.index = props.series.indexOf(item.id);

            return item;
        });

        series = sortBy(seriesResponse, item => {
            return +item.startDate;
        });

        for (const item of series) {
            item.bookingState = await getSeriesBookingState(
                item.attributes?.custom?.subscription_package_id,
                item.season?.attributes?.custom?.use_best_available
            );
        }

        const pricesFrom = series.map(instance => {
            return instance.filters.priceFrom;
        });

        const maxPrice = max(pricesFrom);

        if (maxPrice) {
            state.minPrice = 0;
            state.maxPrice = max(pricesFrom);
        }

        state.selectedPrice = 0;

        setupFilters();

        updateFuse();

        applySearchParams(true);
    }).catch(error => {
        console.log(error)
        state.hasError = true;
    }).finally(() => {
        state.isLoading = false;
    });
});
</script>
